import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "setup";
import { SubmissionModel } from "../shared/submissionModel";
import {
  closeSubmission,
  createSubmission,
  getActiveSubmission,
  submitSubmission,
  updateSubmissionStep,
} from "../shared/submissionThunks";

// The wizard's active submission. The landing page's lists live in SubmissionsListSlice.
// Step slices (steps/FileUpload/FileUploadSlice, …) reset themselves by listening to
// getActiveSubmission.pending, createSubmission.fulfilled and clearActiveSubmission — add new
// step slices to that list rather than clearing their state from here.

type ActiveSubmissionState = {
  ActiveSubmission: SubmissionModel | null;
  LoadingActiveSubmission: boolean;
  ActiveSubmissionLoadFailed: boolean;
  CreatingSubmission: boolean;
};

const initialState: ActiveSubmissionState = {
  ActiveSubmission: null,
  LoadingActiveSubmission: false,
  ActiveSubmissionLoadFailed: false,
  CreatingSubmission: false,
};

export const activeSubmissionSlice = createSlice({
  name: "dataImportActiveSubmission",
  initialState,
  reducers: {
    clearActiveSubmission: (state) => {
      state.ActiveSubmission = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActiveSubmission.pending, (state) => {
        state.LoadingActiveSubmission = true;
        state.ActiveSubmissionLoadFailed = false;
        state.ActiveSubmission = null;
      })
      .addCase(getActiveSubmission.fulfilled, (state, action) => {
        state.ActiveSubmission = action.payload;
        state.LoadingActiveSubmission = false;
      })
      .addCase(getActiveSubmission.rejected, (state) => {
        state.LoadingActiveSubmission = false;
        state.ActiveSubmissionLoadFailed = true;
      })
      .addCase(createSubmission.pending, (state) => {
        state.CreatingSubmission = true;
      })
      .addCase(createSubmission.fulfilled, (state, action) => {
        state.CreatingSubmission = false;
        state.ActiveSubmission = action.payload;
        state.ActiveSubmissionLoadFailed = false;
      })
      .addCase(createSubmission.rejected, (state) => {
        state.CreatingSubmission = false;
      })
      .addCase(updateSubmissionStep.fulfilled, (state, action) => {
        state.ActiveSubmission = action.payload;
      })
      .addCase(submitSubmission.fulfilled, (state, action) => {
        state.ActiveSubmission = action.payload;
      })
      .addCase(closeSubmission.fulfilled, (state, action) => {
        if (state.ActiveSubmission?.submissionID === action.meta.arg) {
          state.ActiveSubmission = null;
        }
      });
  },
});

export const { clearActiveSubmission } = activeSubmissionSlice.actions;

export const selectActiveSubmission = (state: RootState) => state.dataImportActiveSubmission.ActiveSubmission;
export const selectLoadingActiveSubmission = (state: RootState) =>
  state.dataImportActiveSubmission.LoadingActiveSubmission;
export const selectActiveSubmissionLoadFailed = (state: RootState) =>
  state.dataImportActiveSubmission.ActiveSubmissionLoadFailed;
export const selectCreatingSubmission = (state: RootState) => state.dataImportActiveSubmission.CreatingSubmission;

export default activeSubmissionSlice.reducer;
